import React, { useState, useEffect } from 'react';
import '../styles.css';

export default function Dashboard() {
  const [deviceId, setDeviceId] = useState('');
  const [files, setFiles] = useState([]); // { file, previewUrl, isVideo }
  const [statusMessages, setStatusMessages] = useState([]);

  // New state for local‐iframe detection
  const [showLocalIframe, setShowLocalIframe] = useState(false);
  const [checkedLocal, setCheckedLocal] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('deviceId');
    setDeviceId(id || '');
  }, []);

  // Cleanup object URLs when component unmounts
  useEffect(() => {
    return () => {
      files.forEach(f => f.previewUrl && URL.revokeObjectURL(f.previewUrl));
    };
  }, [files]);

  // No‐op effect just to trigger the hidden iframe once on mount
  useEffect(() => {
    // nothing here: the hidden iframe in JSX will fire onLoad/onError
  }, []);

  // Inline mobile-friendly styles (small, non-invasive)
  const styles = {
    container: { padding: '1rem', maxWidth: 720, margin: '0 auto' },
    labelButton: {
      display: 'inline-block',
      padding: '12px 16px',
      borderRadius: 10,
      background: '#0077cc',
      color: 'white',
      fontSize: 16,
      textAlign: 'center',
      width: '100%',
      boxSizing: 'border-box',
      cursor: 'pointer',
      marginBottom: 12,
    },
    inputFile: { display: 'none' },
    submitBtn: {
      display: 'block',
      padding: '12px 16px',
      borderRadius: 10,
      background: '#2d9cdb',
      color: 'white',
      fontSize: 16,
      width: '100%',
      border: 'none',
      cursor: 'pointer',
      marginTop: 12,
    },
    fileList: {
      display: 'flex',
      gap: 8,
      overflowX: 'auto',
      paddingBottom: 8,
    },
    fileItem: {
      minWidth: 80,
      maxWidth: 140,
      textAlign: 'center',
      fontSize: 12,
      borderRadius: 8,
      padding: 6,
      background: '#fafafa',
      border: '1px solid #eee',
      position: 'relative',
    },
    previewImg: {
      width: '100%',
      height: 80,
      objectFit: 'cover',
      borderRadius: 6,
      marginBottom: 6,
    },
    removeBtn: {
      position: 'absolute',
      top: 6,
      right: 6,
      background: 'rgba(0,0,0,0.6)',
      color: 'white',
      border: 'none',
      borderRadius: 12,
      width: 22,
      height: 22,
      lineHeight: '22px',
      textAlign: 'center',
      cursor: 'pointer',
    },
    statusBox: {
      marginTop: 12,
      background: '#f7f7f7',
      padding: 10,
      borderRadius: 8,
      maxHeight: 220,
      overflow: 'auto',
      fontSize: 13,
    },
    smallNote: { fontSize: 13, marginTop: 8, color: '#555' },
  };

  const MAX_FILES = 10;

  const handleFileChange = e => {
    const selected = Array.from(e.target.files || []);

    if (selected.length === 0) {
      setFiles([]);
      setStatusMessages([]);
      return;
    }

    let trimmed = selected;
    if (selected.length > MAX_FILES) {
      trimmed = selected.slice(0, MAX_FILES);
      // user-facing message about trimming
      window.alert(`You selected ${selected.length} files. Only the first ${MAX_FILES} will be used.`);
    }

    // Detect any video files
    const hasVideo = trimmed.some(f => (f.type && f.type.startsWith('video/')) || /\.(mp4|mov|avi|mkv|webm|mpeg|3gp)$/i.test(f.name));
    if (hasVideo) {
      // match user's required alert text
      window.alert(
        'Movie files can take longer to upload. Please make sure you have a reliable internet connection.'
      );
    }

    // Create preview URLs for images (and use a placeholder for video)
    const mapped = trimmed.map(f => {
      const isVideo = Boolean((f.type && f.type.startsWith('video/')) || /\.(mp4|mov|avi|mkv|webm|mpeg|3gp)$/i.test(f.name));
      const previewUrl = !isVideo && (f.type && f.type.startsWith('image/')) ? URL.createObjectURL(f) : null;
      return { file: f, previewUrl, isVideo };
    });

    // Revoke previous preview URLs we are replacing
    files.forEach(old => {
      if (old.previewUrl) URL.revokeObjectURL(old.previewUrl);
    });

    setFiles(mapped);
    setStatusMessages([]);
    // Reset the input value so that re-selecting the same files will trigger onChange
    e.target.value = '';
  };

  const removeFileAt = idx => {
    const copy = files.slice();
    const removed = copy.splice(idx, 1)[0];
    if (removed && removed.previewUrl) URL.revokeObjectURL(removed.previewUrl);
    setFiles(copy);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const messages = [];

    if (!deviceId) {
      setStatusMessages(['Error: device ID missing from URL']);
      return;
    }
    if (files.length === 0) {
      setStatusMessages(['No files selected']);
      return;
    }

    if (!navigator.onLine) {
      setStatusMessages(['Error: No internet connection. Please connect to the internet and try again.']);
      return;
    }

    // iterate through files sequentially, updating status
    for (let i = 0; i < files.length; i++) {
      const { file } = files[i];
      messages.push(`Uploading ${file.name} (${i + 1}/${files.length})…`);
      setStatusMessages([...messages]);

      const formData = new FormData();
      formData.append('file', file);

      try {
        const resp = await fetch(
          `https://api.fotomate.ai/api/upload/${deviceId}`,
          { method: 'POST', body: formData }
        );

        // try to parse JSON if possible
        let data;
        try {
          data = await resp.json();
        } catch (_) {
          data = {};
        }

        if (!resp.ok) {
          throw new Error(data.message || `Status ${resp.status}`);
        }
        messages.push(`Uploaded ${file.name} successfully as ${data.filename || '…'}`);
      } catch (err) {
        messages.push(`Error uploading ${file.name}: ${err.message}`);
      }
      setStatusMessages([...messages]);
    }

    messages.push('All uploads complete');
    setStatusMessages([...messages]);

    // Optionally clear files after upload (comment/uncomment as desired)
    // files.forEach(f => f.previewUrl && URL.revokeObjectURL(f.previewUrl));
    // setFiles([]);
  };

  return (
    <section className="contact" style={styles.container}>
      <h2 style={{ fontSize: 20, marginBottom: 8 }}>Dashboard</h2>

      {!deviceId ? (
        <p style={{ color: 'red' }}>Error: device ID is missing in URL.</p>
      ) : (
        <p style={{ marginBottom: 12 }}>Device ID: <strong>{deviceId}</strong></p>
      )}

      <form onSubmit={handleSubmit}>
        {/* Hidden native file input, replaced by a mobile-friendly button */}
        <label style={styles.labelButton} htmlFor="file-input" aria-label="Select images or videos to upload">
          Tap to select photos & videos (max {MAX_FILES})
        </label>
        <input
          id="file-input"
          type="file"
          multiple
          accept="image/*,video/*"
          onChange={handleFileChange}
          style={styles.inputFile}
          aria-hidden="false"
        />

        {/* File previews / list */}
        {files.length > 0 && (
          <>
            <div style={styles.smallNote}>
              Selected: {files.length} file{files.length !== 1 ? 's' : ''} (you can remove any before uploading)
            </div>

            <div style={styles.fileList} aria-live="polite">
              {files.map((fObj, idx) => (
                <div key={idx} style={styles.fileItem}>
                  <button
                    type="button"
                    aria-label={`Remove ${fObj.file.name}`}
                    onClick={() => removeFileAt(idx)}
                    style={styles.removeBtn}
                  >
                    ×
                  </button>

                  {fObj.previewUrl ? (
                    <img
                      src={fObj.previewUrl}
                      alt={fObj.file.name}
                      style={styles.previewImg}
                    />
                  ) : (
                    <div
                      style={{
                        ...styles.previewImg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#eee',
                        color: '#444',
                      }}
                    >
                      {fObj.isVideo ? '🎬 Video' : 'File'}
                    </div>
                  )}

                  <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {fObj.file.name}
                  </div>
                  <div style={{ color: '#666', fontSize: 11 }}>
                    {(fObj.file.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <button
          type="submit"
          disabled={files.length === 0 || !deviceId}
          style={{
            ...styles.submitBtn,
            opacity: files.length === 0 || !deviceId ? 0.6 : 1,
          }}
        >
          Upload Files
        </button>
      </form>

      <div style={styles.statusBox} className="upload-status" aria-live="polite">
        {statusMessages.length === 0 ? (
          <div style={{ color: '#666' }}>No uploads in progress.</div>
        ) : (
          statusMessages.map((msg, idx) => (
            <div key={idx} style={{ marginBottom: 6 }}>{msg}</div>
          ))
        )}
      </div>

      <div style={{ marginTop: 10, fontSize: 13, color: '#777' }}>
        Note: movie files can be large and may take longer to upload. For large batches consider a fast & reliable Wi-Fi connection.
      </div>

      {/* Hidden iframe to test reachability */}
      {!checkedLocal && (
        <iframe
          src="http://192.168.1.75:8080/"
          style={{ display: 'none' }}
          onLoad={() => {
            setShowLocalIframe(true);
            setCheckedLocal(true);
          }}
          onError={() => {
            setShowLocalIframe(false);
            setCheckedLocal(true);
          }}
          title="local-probe"
        />
      )}

      {/* Only show this if the hidden probe succeeded */}
      {checkedLocal && showLocalIframe && (
        <div style={{ marginTop: '2rem' }}>
          <iframe
            src="http://192.168.1.75:8080/"
            style={{ width: '100%', height: '500px', border: '1px solid #ccc' }}
            title="Local Content"
          />
        </div>
      )}
    </section>
  );
}
